import { atom, type Getter } from 'jotai'
import { ActionType } from '../../../types/FilterCondition'
import {
  UiConditionType,
  UiTrackingConditionTypes,
  type UiFilterCondition,
  type UiFilterDetail,
} from '../filterConditionTypes'
import {
  validateProfileFieldCondition,
  validateUiDeviceCondition,
  validateUiEngagementCondition,
  validateUiGeoCondition,
  validateUiGroupCondition,
  validateUiTrackingCondition,
} from '../utils/conditionValidation'
import {
  validateActivityDate,
  validateUiActivityMessageLinkSpec,
  validateUiActivityMessageSpec,
} from '../utils/activitySpecValidation'
import { filterFormAtom } from './state'

export const dirtyFieldsAtom = atom<string[]>([])

export const isFormValidAtom = atom<boolean>((get: Getter) => {
  const filter = get(filterFormAtom)


  return (
    get(isFormNameValidAtom) &&
    validateBlocksAndConditionsStructure(filter) &&
    validateConditionsBasedOnType(filter)
  )
})

export const isFormNameValidAtom = atom<boolean>((get: Getter) => {
  const name = get(filterFormAtom).Name
  return name.length > 0
})

const validateBlocksAndConditionsStructure = (
  filter: UiFilterDetail,
): boolean => {
  const noBlockisEmpty =
    filter.Blocks.length > 0 &&
    filter.Blocks.every((b) => b.Conditions.length > 0)
  const actionTypesAreSetExceptFirstOne = filter.Blocks.every((b, i) => {
    return (
      (i === 0
        ? b.JoinType === ActionType.Nil
        : b.JoinType === ActionType.And || b.JoinType === ActionType.Or) &&
      b.Conditions.every((c, j) => {
        return j === 0
          ? c.JoinType === ActionType.Nil
          : c.JoinType === ActionType.And || c.JoinType === ActionType.Or
      })
    )
  })
  const eachConditionHasAType = filter.Blocks.every((b) =>
    b.Conditions.every((c) => c.ConditionType !== undefined),
  )
  return (
    noBlockisEmpty && actionTypesAreSetExceptFirstOne && eachConditionHasAType
  )
}

const validateConditionsBasedOnType = (filter: UiFilterDetail): boolean => {
  return filter.Blocks.every((b) =>
    b.Conditions.every((c) => validateCondition(c, filter)),
  )
}

const validateCondition = (
  condition: UiFilterCondition,
  filter: UiFilterDetail,
): boolean => {
  switch (condition.ConditionType) {
    case UiConditionType.GroupBelongs:
    case UiConditionType.GroupNotBelongs:
      return validateUiGroupCondition(condition, filter)
    case UiConditionType.ProfileField:
      return validateProfileFieldCondition(condition)

    case UiConditionType.EmailSubscribed:
    case UiConditionType.EmailUnsubscribed:
      return validateActivityDate(condition)
    case UiConditionType.EmailSent:
    case UiConditionType.EmailSuccessfullySent:
    case UiConditionType.EmailNotSent:
    case UiConditionType.EmailOpened:
    case UiConditionType.EmailNotOpened:
    case UiConditionType.EmailBouncedHB:
    case UiConditionType.EmailBouncedSB:
    case UiConditionType.EmailNotBouncedHB:
    case UiConditionType.EmailPerformedAtLeast1Action:
    case UiConditionType.EmailPerformedLessThan5Actions:
    case UiConditionType.EmailPerformedLessThan10Actions:
    case UiConditionType.EmailPerformed10OrMoreActions:
    case UiConditionType.SmsSent:
    case UiConditionType.SmsNotSent:
    case UiConditionType.SmsSuccessfullySent:
    case UiConditionType.SmsNotReceived:
    case UiConditionType.SmsExpired:
    case UiConditionType.SmsRejected:
      return validateUiActivityMessageSpec(condition, filter)
    case UiConditionType.EmailClicked:
    case UiConditionType.EmailNotClicked:
      return validateUiActivityMessageLinkSpec(condition, filter)

    case UiConditionType.Engagement:
      return validateUiEngagementCondition(condition)
    case UiConditionType.GeoLocatedIn:
    case UiConditionType.GeoNotLocatedIn:
      return validateUiGeoCondition(condition)
    case UiConditionType.Device:
      return validateUiDeviceCondition(condition)
    case UiTrackingConditionTypes.TrackingActive:
    case UiTrackingConditionTypes.TrackingAnonymous:
      return validateUiTrackingCondition(condition)
    default:
      return false
  }
}
